/**
 * problemConverters.js
 *
 * DashboardProblemManagementPageから抽出された純粋関数群
 * 課題管理固有のデータ変換・ID解決ロジックを提供
 */

import { resolveEmployeeIds, toNullableDate } from './converters.js';

/**
 * 複数キーに対して、行データから一意な値を抽出してセレクトボックス用のオプションを生成（課題管理用）
 * @param {Array} rows - データ行の配列
 * @param {Array} keys - 抽出対象のキー名配列
 * @returns {Object} キーごとのオプション配列 { key: ['', value1, value2, ...] }
 */
export function buildSelectOptions(rows, keys) {
  const optionsByKey = Object.fromEntries(keys.map((key) => [key, new Set()]));

  rows.forEach((row) => {
    keys.forEach((key) => {
      const value = row[key];
      if (value === undefined || value === null || value === '') return;
      optionsByKey[key].add(value);
    });
  });

  return Object.fromEntries(
    Object.entries(optionsByKey).map(([key, values]) => [key, ['', ...Array.from(values)]]),
  );
}

/**
 * 課題レコードデータをAPIペイロード形式に変換
 * @param {Object} row - 画面表示用レコード
 * @param {Object} refs - マスタデータのルックアップMap
 * @param {Object} defaults - デフォルト値（oemId, regionId）
 * @returns {Object|null} APIペイロード（必須項目不足時はnull）
 */
export function toPayload(row, refs, defaults) {
  const title = row.summary || '';
  const statusId = refs.statusByName.get(row.status);
  const actorId = refs.actorByName.get(row.ownerRegion);
  let oemId = defaults.oemId;
  let regionId = defaults.regionId;
  if (row.oemRegion && refs.oemRegionLookup.has(row.oemRegion)) {
    const resolved = refs.oemRegionLookup.get(row.oemRegion);
    oemId = resolved.oemId;
    regionId = resolved.regionId;
  }

  const causeId = row.issueOrigin ? (refs.causeByName.get(row.issueOrigin) || null) : null;
  const impactId = row.impact ? (refs.impactByName.get(row.impact) || null) : null;
  const employeeIds = resolveEmployeeIds(row.owner, refs.employeeByName);

  if (!title || !statusId || !actorId || !oemId || !regionId || !causeId || !impactId || !row.dueDate || !row.closeCondition || employeeIds.length === 0) {
    return null;
  }

  // 大中小項目は課題自身のカテゴリとして送る（進捗リンクは進捗管理詳細でのみ管理）。
  // 大項目のみ・大中のみの登録も許可するため、解決できない階層は null にする。
  // 小項目は同名中項目の衝突を避けるため、大項目込みで解決した中項目IDでスコープする。
  const largeCategoryId = row.largeCategory
    ? (refs.largeByName?.get(row.largeCategory) || null)
    : null;
  const middleCategoryId = row.middleCategory
    ? (refs.middleByLargePair?.get(`${row.largeCategory || ''}::${row.middleCategory}`) || null)
    : null;
  const smallCategoryId = (row.smallItem && middleCategoryId != null)
    ? (refs.smallByMiddleIdPair?.get(`${middleCategoryId}::${row.smallItem}`) || null)
    : null;

  return {
    title,
    deadline: toNullableDate(row.dueDate),
    oem_id: oemId,
    region_id: regionId,
    actor_id: actorId,
    status_id: statusId,
    cause_id: causeId,
    impact_id: impactId,
    is_all_problem: Boolean(row.projectIssue),
    large_category_id: largeCategoryId,
    middle_category_id: middleCategoryId,
    small_category_id: smallCategoryId,
    employee_ids: employeeIds,
    risk_ids: (row.riskManagement || '').split(',').map((s) => s.trim()).filter(Boolean)
      .map((name) => refs.riskByName.get(name)).filter(Boolean),
    pmo_confirm_completed_at: row.pmoConfirmCompletedAt || null,
    owner_area_completed_at: row.ownerAreaCompletedAt || null,
    is_risk_emergence: Boolean(row.riskEmergenceIssue),
    issue_by_risk_emergence: row.issueByRiskEmergence || null,
    is_schedule_issue: Boolean(row.scheduleIssue),
    close_condition: row.closeCondition,
    policy_decision_date: row.policyDecisionDate || null,
    policy_detail: row.policyDetail || '',
    todo: Boolean(row.todo),
    updated_at_iso: row.updated_at_iso ?? null,
  };
}
